import { ChevronRight, PanelLeftClose } from 'lucide-react'
import { useEffect, useId, useMemo, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'

import { UnityBadge } from '@/components/brand/Logo'
import { Tooltip } from '@/components/ui/Tooltip'
import { cn } from '@/lib/cn'
import { useAuthStore } from '@/stores/auth'
import { useUIStore } from '@/stores/ui'

import { NAVIGATION, type NavGroup, type NavItem } from './navigation'

function useVisible() {
  const permissions = useAuthStore((state) => state.permissions)
  const isSuperuser = useAuthStore((state) => state.user?.is_superuser ?? false)
  return (item: NavItem) =>
    isSuperuser || item.permissions.some((permission) => permissions.has(permission))
}

/** Every route the sidebar can point at, parents and children alike. */
const NAV_TARGETS = NAVIGATION.flatMap((group) =>
  group.items.flatMap((item) => [item, ...(item.children ?? [])]),
).map(({ to, end }) => ({ to, end: end ?? false }))

/** Whether an entry covers a route: itself, or anything below it. */
function covers(pathname: string, to: string, end: boolean): boolean {
  if (pathname === to) return true
  // `end` marks an entry that owns only its own route — a section whose pages
  // are not sidebar rows, such as a ticket's detail page. `/` is exact for the
  // same reason: as a prefix it would cover the whole console.
  if (end || to === '/') return false
  // The trailing slash keeps the match on a segment boundary, so `/settings`
  // covers `/settings/welcome` but never a sibling like `/settings-export`.
  return pathname.startsWith(`${to}/`)
}

/**
 * The single entry that owns the current route — the most specific one that
 * covers it.
 *
 * Entries overlap: `/settings` covers its own tabs *and* `/settings/permissions`,
 * which has a row of its own. Letting every match light up put two rows in the
 * active state at once, so the deepest match wins and the rest stay quiet.
 */
function useActivePath(): string | undefined {
  const { pathname } = useLocation()
  return useMemo(() => {
    let owner: string | undefined
    for (const { to, end } of NAV_TARGETS) {
      if (covers(pathname, to, end) && (owner === undefined || to.length > owner.length)) {
        owner = to
      }
    }
    return owner
  }, [pathname])
}

/** Which category holds a route, so the page being read never hides in a closed one. */
const GROUP_OF_PATH = new Map<string, string>()
for (const group of NAVIGATION) {
  for (const item of group.items) {
    for (const { to } of [item, ...(item.children ?? [])]) {
      if (!GROUP_OF_PATH.has(to)) GROUP_OF_PATH.set(to, group.id)
    }
  }
}

function NavSection({
  group,
  items,
  collapsed,
  badges,
  first,
}: {
  group: NavGroup
  items: NavItem[]
  collapsed: boolean
  badges: Record<string, number>
  first: boolean
}) {
  const open = useUIStore((state) => state.openNavGroups.includes(group.id))
  const toggle = useUIStore((state) => state.toggleNavGroup)
  const bodyId = useId()

  const rows = items.map((item) => (
    <NavRow key={item.id} item={item} collapsed={collapsed} badges={badges} />
  ))

  // Icons only: there is no heading to click, so every row shows and the
  // categories are told apart by a rule.
  if (!group.heading || collapsed) {
    return (
      <div className="flex flex-col gap-0.5">
        {group.heading && !first && (
          <span className="mx-auto my-1.5 h-px w-6 bg-border" aria-hidden />
        )}
        {rows}
      </div>
    )
  }

  // A closed category still says something is waiting inside it.
  const waiting = open
    ? 0
    : items.reduce((sum, item) => sum + (item.badgeKey ? (badges[item.badgeKey] ?? 0) : 0), 0)

  return (
    <div className="flex flex-col">
      <button
        onClick={() => toggle(group.id)}
        aria-expanded={open}
        aria-controls={bodyId}
        className={cn(
          'group flex items-center gap-1.5 rounded-md px-2.5 py-1.5 transition-colors',
          'text-subtle-foreground hover:text-foreground hover:bg-black/[0.04] dark:hover:bg-white/[0.05]',
        )}
      >
        <ChevronRight
          className={cn('size-3 shrink-0 transition-transform', open && 'rotate-90')}
          strokeWidth={2.25}
          aria-hidden
        />
        <span className="flex-1 truncate text-left text-[10.5px] font-semibold tracking-wider uppercase">
          {group.heading}
        </span>
        {!open && waiting > 0 && (
          <span className="bg-primary-soft text-primary-text tabular rounded-full px-1.5 py-0.5 text-[10.5px] font-semibold">
            {waiting > 99 ? '99+' : waiting}
          </span>
        )}
        {!open && waiting === 0 && (
          <span className="tabular text-[10.5px] opacity-0 transition-opacity group-hover:opacity-100">
            {items.length}
          </span>
        )}
      </button>

      <div
        id={bodyId}
        className={cn(
          'grid transition-[grid-template-rows,opacity] duration-200',
          open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
        )}
        // Closed rows stay out of the tab order and away from screen readers.
        inert={!open}
      >
        <div className="flex min-h-0 flex-col gap-0.5 overflow-hidden pt-0.5">{rows}</div>
      </div>
    </div>
  )
}

function NavRow({
  item,
  collapsed,
  badges,
  depth = 0,
}: {
  item: NavItem
  collapsed: boolean
  badges: Record<string, number>
  depth?: number
}) {
  const activePath = useActivePath()
  const visible = useVisible()
  const children = item.children?.filter(visible) ?? []
  const hasChildren = children.length > 0

  // A parent starts open when the route is already inside it, so a deep link
  // lands with its section expanded.
  const containsRoute = hasChildren && children.some((child) => child.to === activePath)
  const [open, setOpen] = useState(containsRoute)

  const badge = item.badgeKey ? badges[item.badgeKey] : undefined
  const Icon = item.icon

  if (hasChildren) {
    return (
      <div className="flex w-full flex-col">
        <button
          onClick={() => setOpen((value) => !value)}
          className={cn(
            'group flex items-center justify-between rounded-md px-2.5 py-[7px]',
            'text-muted-foreground hover:text-foreground text-[13px] transition-colors',
            'hover:bg-black/[0.04] dark:hover:bg-white/[0.05]',
            collapsed && 'justify-center px-0',
          )}
        >
          <span className="flex min-w-0 items-center gap-2.5">
            <Icon className="size-4 shrink-0" strokeWidth={1.75} aria-hidden />
            {!collapsed && <span className="truncate">{item.label}</span>}
          </span>
          {!collapsed && (
            <ChevronRight
              className={cn('size-3.5 shrink-0 transition-transform', open && 'rotate-90')}
              strokeWidth={2}
              aria-hidden
            />
          )}
        </button>

        {!collapsed && (
          <div
            className={cn(
              'grid transition-[grid-template-rows,opacity] duration-200',
              open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
            )}
          >
            <div className="relative mt-0.5 flex min-h-0 flex-col gap-0.5 overflow-hidden">
              <span
                className="absolute top-0 bottom-0 left-[18px] border-l border-black/5 dark:border-white/5"
                aria-hidden
              />
              {children.map((child) => (
                <NavRow
                  key={child.id}
                  item={child}
                  collapsed={collapsed}
                  badges={badges}
                  depth={depth + 1}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    )
  }

  // Not NavLink: its own matching would still mark `/settings` current on
  // `/settings/permissions`, so the styling and `aria-current` read from the
  // one answer computed above instead of from two disagreeing sources.
  const isActive = item.to === activePath

  const row = (
    <Link
      to={item.to}
      aria-current={isActive ? 'page' : undefined}
      className={cn(
        'group flex items-center justify-between rounded-md py-[7px] text-[13px] transition-colors',
        collapsed ? 'justify-center px-0' : 'px-2.5',
        isActive
          ? 'text-foreground bg-black/[0.055] font-medium dark:bg-white/[0.09]'
          : 'text-muted-foreground hover:text-foreground hover:bg-black/[0.04] dark:hover:bg-white/[0.05]',
      )}
      style={!collapsed && depth > 0 ? { paddingLeft: `${depth * 12 + 10}px` } : undefined}
    >
      <span className="flex min-w-0 items-center gap-2.5">
        <Icon className="size-4 shrink-0" strokeWidth={1.75} aria-hidden />
        {!collapsed && <span className="truncate">{item.label}</span>}
      </span>
      {!collapsed && badge !== undefined && badge > 0 && (
        <span className="bg-primary-soft text-primary-text tabular rounded-full px-1.5 py-0.5 text-[10.5px] font-semibold">
          {badge > 99 ? '99+' : badge}
        </span>
      )}
      {collapsed && badge !== undefined && badge > 0 && (
        <span className="bg-primary absolute top-1 right-1 size-1.5 rounded-full" aria-hidden />
      )}
    </Link>
  )

  return collapsed ? (
    <Tooltip content={item.label} side="right">
      <span className="relative block w-full">{row}</span>
    </Tooltip>
  ) : (
    row
  )
}

export function Sidebar({ badges }: { badges: Record<string, number> }) {
  const collapsed = useUIStore((state) => state.sidebarCollapsed)
  const toggle = useUIStore((state) => state.toggleSidebar)
  const visible = useVisible()
  const openNavGroup = useUIStore((state) => state.openNavGroup)
  const activePath = useActivePath()

  // Arriving on a page — a deep link, the command palette — opens its category.
  // Only on arrival: once there, the category may still be closed by hand.
  const activeGroup = activePath === undefined ? undefined : GROUP_OF_PATH.get(activePath)
  useEffect(() => {
    if (activeGroup) openNavGroup(activeGroup)
  }, [activeGroup, openNavGroup])

  return (
    <aside
      className={cn(
        'bg-sidebar border-sidebar-border flex h-full shrink-0 flex-col border-r',
        'transition-[width] duration-200 ease-out',
        collapsed ? 'w-14' : 'w-60',
      )}
    >
      <div
        className={cn(
          'flex h-14 shrink-0 items-center gap-2.5 border-b border-sidebar-border px-3',
          collapsed && 'justify-center px-0',
        )}
      >
        <UnityBadge className="size-7" />
        {!collapsed && (
          <div className="min-w-0 flex-1">
            <p className="truncate text-[13px] leading-tight font-semibold">Unity RP</p>
            <p className="text-subtle-foreground text-[11px] leading-tight">Staff console</p>
          </div>
        )}
        {!collapsed && (
          <button
            onClick={toggle}
            className="text-subtle-foreground hover:text-foreground -mr-1 rounded-md p-1.5 transition-colors"
            aria-label="Collapse sidebar"
          >
            <PanelLeftClose className="size-4" strokeWidth={1.75} />
          </button>
        )}
      </div>

      <nav
        className={cn(
          'scrollbar-none flex flex-1 flex-col overflow-y-auto p-2',
          collapsed ? 'gap-0' : 'gap-1',
        )}
      >
        {NAVIGATION.map((group, index) => {
          const items = group.items.filter(
            (item) => visible(item) || (item.children?.some(visible) ?? false),
          )
          if (items.length === 0) return null

          return (
            <NavSection
              key={group.id}
              group={group}
              items={items}
              collapsed={collapsed}
              badges={badges}
              first={index === 0}
            />
          )
        })}
      </nav>

      {collapsed && (
        <button
          onClick={toggle}
          className="text-subtle-foreground hover:text-foreground mx-auto mb-3 rounded-md p-1.5 transition-colors"
          aria-label="Expand sidebar"
        >
          <PanelLeftClose className="size-4 rotate-180" strokeWidth={1.75} />
        </button>
      )}
    </aside>
  )
}
